import { datadogRum } from '@datadog/browser-rum'
import type {
  AssertiveYieldManagerEnv,
  AyRefreshControl,
} from '../../types/assertive'
import { withAyManager } from '../../utils/ad-ops/assertive-yield/queue'
import { onSlotEvent, slotState, type SlotEvents } from './slots'

// The app refreshes an opted-in programmatic slot only while a reader can see
// it. See ADOPS_RUNBOOK.md, "Programmatic slot refresh".

export const REFRESH_MAX_FLAG = 'adRefreshMax'
export const REFRESH_SECONDS_FLAG = 'adRefreshSeconds'

// Bidders and AY's own rules treat refreshing faster than this as abuse.
const MIN_REFRESH_SECONDS = 30

/** How many times, and how often, the app refreshes a slot. */
export type RefreshPolicy = { max: number; seconds: number }

// AY's rule on Discover today: 30s × 3.
export const DEFAULT_REFRESH: RefreshPolicy = { max: 3, seconds: 30 }

const TICK_MS = 1_000
const IN_VIEW_PERCENT = 50
// The input events VueUse's useIdle listens for.
const INPUT_EVENTS = [
  'mousemove',
  'mousedown',
  'resize',
  'keydown',
  'touchstart',
  'wheel',
]

/** The refresh count a flag value allows: 0 is off, anything unusable the default. */
export function refreshMax(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return DEFAULT_REFRESH.max
  }
  return Math.floor(value)
}

/** The refresh interval a flag value asks for, never under the minimum. */
export function refreshSeconds(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return DEFAULT_REFRESH.seconds
  }
  return Math.max(MIN_REFRESH_SECONDS, Math.round(value))
}

type EnvSettings = Pick<AssertiveYieldManagerEnv, 'settings'> | undefined

/**
 * AY refreshes a placement unless its own refresh is off on every one of the
 * placement's ad units. Anything missing means AY refreshes, so a slot never
 * has two refreshers.
 */
export function ayRefreshesPlacement(
  env: EnvSettings,
  placement: string
): boolean {
  const settings = env?.settings
  const unitIds =
    settings?.placements?.find((p) => p.code === placement)?.adUnits ?? []
  if (unitIds.length === 0) return true

  const controls = settings?.definitions?.refreshControls ?? {}
  return unitIds.some((id) => {
    const refresh = settings?.adUnits?.find((unit) => unit._id === id)?.refresh
    if (!refresh) return true
    const assigned = (refresh.refreshControls ?? []).map(
      (controlId) => controls[controlId]
    )
    if (assigned.some((control) => !control || controlRefreshes(control))) {
      return true
    }
    // The dashboard won't leave Basic Refresh empty; a control without
    // conditions replaces it.
    const replacesBasic = assigned.some(
      (control) => !control?.conditions?.length
    )
    return !replacesBasic && (refresh.refreshBasic ?? 0) > 0
  })
}

/** Anything but "No Refresh": 0s, 0 calls and no rules. */
function controlRefreshes(control: AyRefreshControl): boolean {
  return (
    control.defaultRefresh !== 0 ||
    control.refreshCallsLimit !== 0 ||
    Boolean(control.rules?.length)
  )
}

type Refresher = Pick<AssertiveYieldManagerEnv, 'refresh'>

type Entry = {
  env: Refresher
  max: number
  intervalMs: number
  count: number
  /** Time the slot has been refreshable without a break. */
  eligibleMs: number
  /** A refresh was asked for and its render hasn't ended. */
  pending: boolean
  pendingSince: number
}

// AY can drop a refresh, e.g. for a slot it has destroyed, so the render a
// slot waits for may never come. A refresh renders in about 1.5s.
const PENDING_TIMEOUT_MS = 10_000

const entries = new Map<string, Entry>()
let ticker: ReturnType<typeof setInterval> | null = null
let stopRenderWatch: (() => void) | null = null
let lastInputAt = 0
let appRefreshing = false

function onInput(): void {
  lastInputAt = Date.now()
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') onInput()
}

function start(): void {
  if (ticker) return
  lastInputAt = Date.now()
  for (const name of INPUT_EVENTS) {
    window.addEventListener(name, onInput, { passive: true })
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  stopRenderWatch = onSlotEvent('slotRenderEnded', onRenderEnded)
  ticker = setInterval(tick, TICK_MS)
}

function stop(): void {
  if (ticker) clearInterval(ticker)
  ticker = null
  for (const name of INPUT_EVENTS) window.removeEventListener(name, onInput)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  stopRenderWatch?.()
  stopRenderWatch = null
}

function tick(): void {
  const now = Date.now()
  const tabVisible = document.visibilityState === 'visible'
  for (const [code, entry] of entries) {
    const state = slotState(code)
    if (state.fellBack) {
      unregisterFromRefresh(code)
      continue
    }
    if (entry.pending) {
      if (now - entry.pendingSince < PENDING_TIMEOUT_MS) continue
      entry.pending = false
      if (entry.count >= entry.max) {
        unregisterFromRefresh(code)
        continue
      }
    }
    if (state.filled !== true) continue
    // The reader counts as active for one interval after their last input.
    const active = tabVisible && now - lastInputAt <= entry.intervalMs
    if (!active || state.inViewPercent < IN_VIEW_PERCENT) {
      entry.eligibleMs = 0
      continue
    }
    entry.eligibleMs += TICK_MS
    if (entry.eligibleMs < entry.intervalMs) continue

    entry.eligibleMs = 0
    entry.pending = true
    entry.pendingSince = now
    entry.count += 1
    withAyManager(() => {
      if (entries.get(code) !== entry) return
      entry.env.refresh([code], {
        useInstanceCodes: true,
        ignoreRefreshLimits: true,
      })
    })
  }
}

function onRenderEnded(event: SlotEvents['slotRenderEnded']): void {
  const code = event.slot.getSlotElementId()
  const entry = entries.get(code)
  if (!entry?.pending) return
  entry.pending = false
  // An empty refresh has handed the slot to the native fallback.
  if (event.isEmpty || entry.count >= entry.max) unregisterFromRefresh(code)
}

/**
 * Refreshes `code` per `policy`, counting once its render ends filled. A
 * later registration replaces an earlier one: AY reused the code.
 */
export function registerForRefresh(
  code: string,
  policy: RefreshPolicy,
  env: Refresher
): void {
  if (policy.max <= 0) return
  entries.set(code, {
    env,
    max: policy.max,
    intervalMs: policy.seconds * 1000,
    count: 0,
    eligibleMs: 0,
    pending: false,
    pendingSince: 0,
  })
  start()
  if (!appRefreshing) {
    appRefreshing = true
    datadogRum.setGlobalContextProperty('ad_app_refresh', true)
  }
}

/**
 * Refreshes the slot defined under `code` per `policy`, unless AY's own
 * refresh still covers its placement: a slot never has two refreshers.
 */
export function refreshUnlessAyDoes(
  code: string,
  placement: string,
  policy: RefreshPolicy,
  env: Pick<AssertiveYieldManagerEnv, 'settings' | 'refresh'>
): void {
  if (policy.max <= 0 || ayRefreshesPlacement(env, placement)) return
  registerForRefresh(code, policy, env)
}

export function unregisterFromRefresh(code: string): void {
  entries.delete(code)
  if (entries.size === 0) stop()
}

// RUM vitals carry the refresh arm the page ran, like the slot margins.
export function tagRumWithRefresh(policy: RefreshPolicy): void {
  datadogRum.setGlobalContextProperty('ad_refresh_max', policy.max)
  datadogRum.setGlobalContextProperty('ad_refresh_seconds', policy.seconds)
}

export function resetRefreshForTests(): void {
  entries.clear()
  stop()
  lastInputAt = 0
  appRefreshing = false
}
